/**
 * @fileoverview React Context for Listings Launch data.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */
import {MatchedListing} from '/src/com/yext/listingslaunchstorm/js/data/matchedlisting';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerifiedListing} from '/src/com/yext/listingslaunchstorm/js/data/verifiedlisting';
import {ViewSmbLaunchOptions} from '/src/com/yext/listingslaunchstorm/js/data/viewsmblaunchoptions';

const ListingsLaunchContext = React.createContext(undefined);

/**
 * @param {{
 *  options: !ViewSmbLaunchOptions,
 *  children: (React.ReactNode | undefined),
 * }} props
 */
const ListingsLaunchContextProvider = ({options, children}) => {
  const linkedEntities = options.entities;

  const [googleGroups, setGoogleGroups] = React.useState([]);
  const [matchedListings, setMatchedListings] =
    React.useState(getInitialMatchedListings(options.gbpConfigurations));
  const [verifiedListings, setVerifiedListings] =
    React.useState(getInitialVerifiedListings(options.gbpConfigurations));
  const currentMatches = options.gbpConfigurations.filter(gbpConfiguration => !!gbpConfiguration.externalId).reduce((set, currentMatch) => {
    set.add(currentMatch.externalId);
    return set;
  }, new Set());

  const taskflowContextValue = React.useMemo(() => ({
    linkedEntities,
    googleGroups,
    setGoogleGroups,
    matchedListings,
    setMatchedListings,
    verifiedListings,
    setVerifiedListings,
    currentMatches,
  }), [
    linkedEntities,
    googleGroups,
    matchedListings,
    verifiedListings,
    currentMatches,
  ]);

  return (
    <ListingsLaunchContext.Provider value={taskflowContextValue}>
      {children}
    </ListingsLaunchContext.Provider>
  );
};

function getInitialMatchedListings(gbpConfigurations) {
  return gbpConfigurations
    // Present External IDs at this stage imply the listing already has a match
    .filter(c => !!c.externalId)
    .map(c => new MatchedListing({
      entityId: c.entityId,
      groupId: c.groupId,
      externalId: c.externalId,
      displayAddress: yext.msg('Existing Match'),
      isVerified: c.isVerified,
    }));
}

function getInitialVerifiedListings(gbpConfigurations) {
  return gbpConfigurations
    .filter(c => c.isVerified)
    .map(c => new VerifiedListing({
      entityId: c.entityId,
      method: VerificationMethod.OTHER,
    }));
}

export {ListingsLaunchContext, ListingsLaunchContextProvider};
